import { useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppState } from '../../../AppStateProvider';
import type { Contact } from '../../../domain/contact';
import { Page } from '../../../domain/page';
import { contactsApi } from '../api';
import linkedinContactQueryKey from '../linkedinContact/linkedinContactQueryKey';
import { CONTACT_KEY } from '../repository';
import { createLinkedinContactsPayload } from '../utils/createLinkedinContactsPayload';

import { makeLinkedinContactsListKey } from './linkedinContactsListQueryKey';

type useLinkedinContactsListQueryReturnProps = {
  data: Partial<Contact>[];
  isLoading: boolean;
  isError: boolean;
};

const DATA_WAITING_TIME = 3000;

export const useLinkedinContactsListQuery = (): useLinkedinContactsListQueryReturnProps => {
  const queryClient = useQueryClient();
  const appState = useAppState();
  const { isOpen, collectedContactsSearchInfo = [], page } = appState;
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => setHasTimedOut(true), DATA_WAITING_TIME);
    return () => clearTimeout(timeoutId);
  }, []);

  const getContactByLinkedinIdFromCache = (contactId: string) => {
    let contact: Contact | undefined;

    queryClient.getQueriesData<Contact>({
      queryKey: [CONTACT_KEY],
      exact: false,
      predicate: (query) => {
        if (query.state.data?.linkedinId === contactId || query.state.data?.linkedinSalesNavId === contactId) {
          contact = query.state.data;
          return true;
        }
        return false;
      },
    });

    return contact;
  };

  const fetchLinkedinContactsList = async ({ signal }: { signal?: AbortSignal }) => {
    const filteredContacts = collectedContactsSearchInfo.filter((collectedContact) => {
      const contactId = collectedContact.linkedinId || collectedContact.linkedinSalesNavId;
      if (!contactId) {
        return true;
      }

      const contactFromCache = getContactByLinkedinIdFromCache(contactId);
      if (contactFromCache) {
        return false;
      }

      queryClient.setQueryData(linkedinContactQueryKey.fromLinkedinId(contactId), {
        linkedinId: collectedContact.linkedinId,
        linkedinSalesNavId: collectedContact.linkedinSalesNavId,
      });

      return true;
    });

    const payload = createLinkedinContactsPayload(filteredContacts);

    if (payload.length === 0) {
      return [];
    }

    const contacts = await contactsApi.getLinkedinContacts(payload, page === Page.Search ? undefined : signal);

    if (contacts) {
      payload.forEach((payloadContact, index) => {
        const contactData = contacts?.[index];

        if (contactData) {
          const payloadContactId = payloadContact.linkedinId || payloadContact.linkedinSalesNavId;
          if (payloadContactId) {
            queryClient.removeQueries({
              queryKey: linkedinContactQueryKey.fromLinkedinId(payloadContactId),
              exact: true,
            });
          }

          const contactDataToSet = {
            ...contactData,
            linkedinId: contactData.linkedinId ?? payloadContact.linkedinId,
            linkedinSalesNavId: contactData.linkedinSalesNavId ?? payloadContact.linkedinSalesNavId,
          };
          queryClient.setQueryData(linkedinContactQueryKey.fromData(contactDataToSet), contactDataToSet);
        }
      });
    }

    return contacts || [];
  };

  const { isLoading, isError } = useQuery({
    queryKey: makeLinkedinContactsListKey(appState),
    queryFn: fetchLinkedinContactsList,
    enabled: isOpen && collectedContactsSearchInfo.length > 0,
  });

  return {
    data: collectedContactsSearchInfo.map((collectedContact: Partial<Contact>) => {
      const contactId = collectedContact.linkedinId || collectedContact.linkedinSalesNavId;
      return {
        ...collectedContact,
        ...(contactId ? getContactByLinkedinIdFromCache(contactId) : {}),
      };
    }),
    isLoading: isLoading || (!hasTimedOut && collectedContactsSearchInfo.length === 0),
    isError,
  };
};
